import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import PlusIcon from '../assets/Icons/plus.svg';
import EditIcon from '../assets/Icons/reload.svg';
import TrashIcon from '../assets/Icons/black_X.svg';
import CloseIcon from '../assets/Icons/black_X.svg';

// Keep in sync with client/src/tenants/sharedComponents/gister/gisterComponentRegistry.jsx
const GISTER_COMPONENT_OPTIONS = [
  { key: 'personalInformation', label: "Patient's Personal Information" },
  { key: 'doctorMedicalConcentrate', label: 'Select doctor and type of laxative solution' },
  { key: 'procedureDetails', label: 'Procedure Details' },
  { key: 'medicalConcentrate', label: 'Medical Concentrate Selection' },
  { key: 'tavrProcedureSection', label: "Patient's Procedure (TAVR)" },
  { key: 'tavrMedicationSection', label: "Patient's Medications (TAVR)" },
];

const Container = styled.div`
  padding: 2rem;
  background: #f5f5f5;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
  }
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #f02a4c;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s;

  img {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: #d4223f;
  }

  &.secondary {
    background: white;
    color: #333;
    border: 1px solid #e5e5e5;

    &:hover {
      background: #f5f5f5;
    }
  }
`;

const FacilityHeader = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 2rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const FacilityLogo = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 16px;
  background: ${props => props.$logo ? 'transparent' : 'linear-gradient(135deg, #84a4fc, #4A90E2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const FacilityInitials = styled.span`
  color: white;
  font-size: 3rem;
  font-weight: 700;
`;

const FacilityInfo = styled.div`
  flex: 1;
`;

const FacilityName = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.5rem;
`;

const FacilityStats = styled.div`
  display: flex;
  gap: 2rem;
  color: #666;
  font-size: 0.9rem;
`;

const Stat = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatValue = styled.span`
  font-weight: 600;
  color: #333;
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: ${props => props.small ? '0.4rem 0.75rem' : '0.5rem 1rem'};
  background: #f02a4c;
  border: none;
  border-radius: 6px;
  font-size: ${props => props.small ? '0.8rem' : '0.85rem'};
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};

  img {
    width: ${props => props.small ? '12px' : '14px'};
    height: ${props => props.small ? '12px' : '14px'};
  }

  &:hover:not(:disabled) {
    background: #d4223f;
  }
`;

const DepartmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DepartmentCard = styled.div`
  background: #f9f9f9;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e5e5e5;
`;

const DepartmentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const DepartmentName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #333;
`;

const DepartmentActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const SmallActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  img {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: #f5f5f5;
  }
`;

const StaffList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const StaffItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  background: white;
  border-radius: 6px;
  border: 1px solid #e5e5e5;
`;

const StaffInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const StaffName = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
`;

const StaffEmail = styled.div`
  font-size: 0.8rem;
  color: #666;
`;

const StaffRole = styled.div`
  font-size: 0.75rem;
  color: #999;
`;

const StaffActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const AddStaffButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #84a4fc;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  color: white;
  cursor: pointer;
  margin-top: 0.75rem;
  transition: all 0.2s;

  img {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: #4A90E2;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #999;
  font-size: 0.9rem;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem;
  border-bottom: 1px solid #e5e5e5;

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #333;
    margin: 0;
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  img {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: #f5f5f5;
  }
`;

const ModalBody = styled.div`
  padding: 1.25rem;
`;

const FormField = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #84a4fc;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #84a4fc;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem;
  border-top: 1px solid #e5e5e5;
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: 1px solid #e5e5e5;
  background: white;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
  }
`;

const LogoUploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const LogoPreview = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: #f5f5f5;
  border: 1px solid #e5e5e5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;

  span {
    font-size: 1.3rem;
    font-weight: 700;
    color: #999;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const UploadButtonLabel = styled.label`
  display: inline-flex;
  align-items: center;
  padding: 0.65rem 1.25rem;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #ccc;
  }
`;

const ErrorText = styled.div`
  font-size: 0.75rem;
  color: #d4223f;
  margin-top: 0.4rem;
`;

const SuccessText = styled.div`
  font-size: 0.75rem;
  color: #2e7d32;
  margin-top: 0.4rem;
`;

const HintText = styled.div`
  font-size: 0.75rem;
  color: #999;
  margin-top: 0.4rem;
`;

const LocationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const LocationRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const LocationInput = styled(Input)`
  flex: 1;
`;

const AddLocationButton = styled.button`
  align-self: flex-start;
  padding: 0.5rem 0.9rem;
  border: 1px dashed #84a4fc;
  background: #f6f8ff;
  color: #4A4A4A;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;

  &:hover {
    background: #eef2ff;
  }
`;

const RemoveLocationButton = styled.button`
  flex-shrink: 0;
  padding: 0.55rem 0.8rem;
  border: 1px solid #e5e5e5;
  background: white;
  color: #a33;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;

  &:hover {
    background: #fff5f5;
  }
`;

const BrandingRow = styled.div`
  display: flex;
  gap: 0.5rem;

  input {
    flex: 1;
  }
`;

const FetchButton = styled.button`
  padding: 0.5rem 1rem;
  background: #84a4fc;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #4A90E2;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SettingsCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const SettingsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;

  h3 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
  }
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SettingsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ColorPicker = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
`;

const ColorField = styled.div`
  label {
    display: block;
    margin-bottom: 0.4rem;
    font-size: 0.85rem;
    font-weight: 600;
    color: #333;
  }
`;

const ColorInput = styled.input`
  width: 100%;
  height: 42px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 0.2rem;
  cursor: pointer;
`;

const GisterComponentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 0.75rem;
`;

const GisterComponentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.85rem;

  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
  }
`;

const ReorderButtons = styled.div`
  display: flex;
  gap: 0.25rem;

  button {
    width: 22px;
    height: 22px;
    border: 1px solid #e5e5e5;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.75rem;
    line-height: 1;

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      background: #f5f5f5;
    }
  }
`;

const SaveButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #f02a4c;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #d4223f;
  }
`;

// New layout styles
const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 1.5rem;
  padding: 0 2rem 2rem;
`;

const Column = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  background: #f9f9f9;
  border-bottom: 1px solid #e5e5e5;

  h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #333;
  }
`;

const SimpleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
`;

const SimpleTh = styled.th`
  padding: 0.875rem 1.5rem;
  text-align: ${props => props.align || 'left'};
  font-weight: 600;
  color: #666;
  background: #f5f5f5;
  border-bottom: 1px solid #e5e5e5;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SimpleTr = styled.tr`
  transition: background 0.15s;

  &:hover {
    background: #f9f9f9;
  }

  &:not(:last-child) td {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const SimpleTd = styled.td`
  padding: 1rem 1.5rem;
  text-align: ${props => props.align || 'left'};
  color: #333;
  vertical-align: middle;
`;

const StaffCount = styled.div`
  font-size: 0.8rem;
  color: #999;
  margin-top: 0.25rem;
`;

const StaffCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: #e3f2fd;
  color: #1976d2;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid ${props => props.danger ? '#ffcdd2' : '#e5e5e5'};
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;

  img {
    width: 12px;
    height: 12px;
  }

  &:hover {
    background: ${props => props.danger ? '#ffebee' : '#f5f5f5'};
    border-color: ${props => props.danger ? '#ef9a9a' : '#ccc'};
  }
`;

export default function FacilityDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(null);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [editingFacility, setEditingFacility] = useState(null);
  const [originalLocations, setOriginalLocations] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [facilityError, setFacilityError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [brandingUrl, setBrandingUrl] = useState('');
  const [fetchingBranding, setFetchingBranding] = useState(false);

  useEffect(() => {
    fetchFacility();
  }, [id]);

  useEffect(() => {
    if (facility) {
      setEditingFacility({
        name: facility.name || '',
        logo: facility.logo || '',
        color1: facility.color1 || '#84a4fc',
        color2: facility.color2 || '#4A90E2',
        color3: facility.color3 || '#f02a4c',
        language: facility.language || 'en',
        gisterConfig: facility.gisterConfig?.length ? facility.gisterConfig : [],
        locations: facility.locations?.length ? facility.locations : [],
      });
    }
  }, [facility]);

  const fetchFacility = async () => {
    try {
      const { data } = await axios.get(`/api/admin/hospitals/${id}`);
      if (data.success) {
        setFacility(data.data);
        setOriginalLocations(data.data.locations || []);
      }
    } catch (error) {
      console.error('Failed to fetch facility:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDepartment = () => {
    setEditingDepartment({ name: '', config: { patientIdentifierType: 'ssn' } });
    setModalOpen('department');
  };

  const handleEditDepartment = (department) => {
    setEditingDepartment({
      ...department,
      config: { patientIdentifierType: 'ssn', ...(department.config || {}) },
    });
    setModalOpen('department');
  };

  const handleSaveDepartment = async () => {
    try {
      if (editingDepartment.id) {
        await axios.put(`/api/admin/departments/${editingDepartment.id}`, editingDepartment);
      } else {
        await axios.post(`/api/admin/hospitals/${id}/departments`, editingDepartment);
      }
      setModalOpen(null);
      setEditingDepartment(null);
      fetchFacility();
    } catch (error) {
      console.error('Failed to save department:', error);
    }
  };

  const handleDeleteDepartment = async (deptId, name, staffCount) => {
    if (!confirm(`Are you sure you want to delete "${name}"?\n\nThis will also delete ${staffCount} staff members.\n\nThis action cannot be undone.`)) return;
    try {
      await axios.delete(`/api/admin/departments/${deptId}`);
      fetchFacility();
    } catch (error) {
      console.error('Failed to delete department:', error);
    }
  };

  const handleCreateStaff = (departmentId) => {
    setEditingStaff({ name: '', email: '', phoneNumber: '', password: '', role: 'coordinator', departmentId, hospitalId: id });
    setModalOpen('staff');
  };

  const handleEditStaff = (staff) => {
    setEditingStaff({ ...staff, password: '' });
    setModalOpen('staff');
  };

  const handleSaveStaff = async () => {
    try {
      if (editingStaff.id) {
        await axios.put(`/api/admin/staff/${editingStaff.id}`, editingStaff);
      } else {
        await axios.post('/api/admin/staff', editingStaff);
      }
      setModalOpen(null);
      fetchFacility();
    } catch (error) {
      console.error('Failed to save staff:', error);
    }
  };

  const handleDeleteStaff = async (staffId) => {
    if (!confirm('Are you sure you want to delete this staff member?')) return;
    try {
      await axios.delete(`/api/admin/staff/${staffId}`);
      fetchFacility();
    } catch (error) {
      console.error('Failed to delete staff:', error);
    }
  };

  const toggleGisterComponent = (key) => {
    setEditingFacility(prev => {
      const current = prev?.gisterConfig || [];
      const isSelected = current.includes(key);
      return {
        ...prev,
        gisterConfig: isSelected ? current.filter(k => k !== key) : [...current, key],
      };
    });
  };

  const moveGisterComponent = (key, direction) => {
    setEditingFacility(prev => {
      const current = [...(prev?.gisterConfig || [])];
      const index = current.indexOf(key);
      const newIndex = index + direction;
      if (index === -1 || newIndex < 0 || newIndex >= current.length) return prev;
      [current[index], current[newIndex]] = [current[newIndex], current[index]];
      return { ...prev, gisterConfig: current };
    });
  };

  const handleAddLocation = () => {
    setEditingFacility(prev => ({
      ...prev,
      locations: [...(prev?.locations || []), ''],
    }));
  };

  const handleUpdateLocation = (index, value) => {
    setEditingFacility(prev => ({
      ...prev,
      locations: (prev?.locations || []).map((location, locationIdx) => (
        locationIdx === index ? value : location
      )),
    }));
  };

  const handleRemoveLocation = (index) => {
    setEditingFacility(prev => ({
      ...prev,
      locations: (prev?.locations || []).filter((_, locationIdx) => locationIdx !== index),
    }));
  };

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFacilityError('');
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      formData.append('name', editingFacility?.name || facility.name || '');
      const { data } = await axios.post('/api/admin/hospitals/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.success) {
        setEditingFacility(prev => ({ ...prev, logo: data.data.logo }));
      } else {
        setFacilityError('Failed to upload logo');
      }
    } catch (error) {
      console.error('Failed to upload logo:', error);
      setFacilityError('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleFetchBranding = async () => {
    if (!brandingUrl) return;
    setFacilityError('');
    setFetchingBranding(true);
    try {
      const { data } = await axios.post('/api/admin/hospitals/fetch-branding', {
        url: brandingUrl,
        name: editingFacility?.name || facility.name || '',
      });
      if (data.success) {
        setEditingFacility(prev => ({
          ...prev,
          logo: data.data.logo || prev.logo,
          color1: data.data.color1 || prev.color1,
          color2: data.data.color2 || prev.color2,
          color3: data.data.color3 || prev.color3,
        }));
      } else {
        setFacilityError(data.details || data.error || 'Failed to fetch branding from website');
      }
    } catch (error) {
      console.error('Failed to fetch branding:', error);
      const details = error.response?.data?.details || error.response?.data?.error;
      setFacilityError(details || 'Failed to fetch branding from website');
    } finally {
      setFetchingBranding(false);
    }
  };

  const computeLocationChanges = () => {
    const locations = Array.from(new Set(
      (editingFacility?.locations || [])
        .map(location => location.trim())
        .filter(Boolean)
    ));

    const removed = (originalLocations || []).filter(loc => !locations.includes(loc));

    // Detect in-place renames by index. Only count a change as a rename when the
    // old value no longer appears anywhere in the new list and the new value did
    // not already exist in the original list. This avoids treating reorderings as renames.
    const renames = {};
    const maxIdx = Math.min(originalLocations.length, locations.length);
    for (let i = 0; i < maxIdx; i++) {
      const oldLoc = originalLocations[i];
      const newLoc = locations[i];
      if (oldLoc && newLoc && oldLoc !== newLoc) {
        if (!locations.includes(oldLoc) && !originalLocations.includes(newLoc)) {
          renames[oldLoc] = newLoc;
        }
      }
    }

    const pendingRemovals = removed.filter(loc => !renames[loc]);

    return { locations, removed, renames, pendingRemovals };
  };

  const handleSaveFacility = async () => {
    try {
      const { locations, renames, pendingRemovals } = computeLocationChanges();
      const cleanedEditingFacility = { ...editingFacility, locations };

      // Renames are applied in place; removals clear the doctor location
      // immediately without asking.
      const updates = [
        ...Object.entries(renames).map(([from, to]) => ({ fromLocation: from, toLocation: to })),
        ...pendingRemovals.map(from => ({ fromLocation: from, toLocation: null })),
      ];

      if (updates.length > 0) {
        await axios.post('/api/admin/doctors/bulk-update-locations', { hospitalId: id, updates });
      }

      await saveFacility(cleanedEditingFacility);
    } catch (error) {
      console.error('Failed to save facility:', error);
      setFacilityError('Failed to save changes');
      setSaveSuccess(false);
    }
  };

  const saveFacility = async (facilityData) => {
    await axios.put(`/api/admin/hospitals/${id}`, facilityData);
    setBrandingUrl('');
    setFacilityError('');
    await fetchFacility();
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleDeleteFacility = async () => {
    const departmentsCount = facility.Departments?.length || 0;
    const staffCount = facility.Departments?.reduce((sum, d) => sum + (d.StaffMembers?.length || 0), 0) || 0;
    
    if (!confirm(`Are you sure you want to delete "${facility.name}"?\n\nThis will also delete:\n- ${departmentsCount} departments\n- ${staffCount} staff members\n\nThis action cannot be undone.`)) return;
    
    try {
      await axios.delete(`/api/admin/hospitals/${id}`);
      navigate('/admin');
    } catch (error) {
      console.error('Failed to delete facility:', error);
    }
  };

  if (loading) return <Container>Loading...</Container>;
  if (!facility) return <Container>Facility not found</Container>;

  const totalStaff = facility.Departments?.reduce((sum, d) => sum + (d.StaffMembers?.length || 0), 0) || 0;

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/admin')}>
          ← Back
        </BackButton>
        <Title>{facility.name}</Title>
        <Actions>
          <ActionButton className="secondary" onClick={handleDeleteFacility}>
            <img src={TrashIcon} alt="" />
            Delete
          </ActionButton>
        </Actions>
      </Header>

      <FacilityHeader>
        <FacilityLogo $logo={facility.logo}>
          {facility.logo ? <img src={facility.logo} alt={facility.name} /> : <FacilityInitials>{facility.name?.[0]}</FacilityInitials>}
        </FacilityLogo>
        <FacilityInfo>
          <FacilityName>{facility.name}</FacilityName>
          <FacilityStats>
            <Stat>
              <span>Departments:</span>
              <StatValue>{facility.Departments?.length || 0}</StatValue>
            </Stat>
            <Stat>
              <span>Staff:</span>
              <StatValue>{totalStaff}</StatValue>
            </Stat>
          </FacilityStats>
        </FacilityInfo>
      </FacilityHeader>

      <SettingsCard>
        <SettingsHeader>
          <h3>Facility Settings</h3>
          <SaveButton onClick={handleSaveFacility}>Save Changes</SaveButton>
        </SettingsHeader>

        {facilityError && <ErrorText>{facilityError}</ErrorText>}
        {saveSuccess && <SuccessText>Changes saved successfully</SuccessText>}

        <SettingsGrid>
          <SettingsColumn>
            <FormField>
              <label>Name</label>
              <Input
                value={editingFacility?.name || ''}
                onChange={e => setEditingFacility({ ...editingFacility, name: e.target.value })}
                placeholder="Facility name"
              />
            </FormField>

            <FormField>
              <label>Auto-fill from company website</label>
              <BrandingRow>
                <Input
                  value={brandingUrl}
                  onChange={e => setBrandingUrl(e.target.value)}
                  placeholder="https://company-website.com"
                />
                <FetchButton
                  type="button"
                  disabled={!brandingUrl || !editingFacility?.name || fetchingBranding}
                  onClick={handleFetchBranding}
                >
                  {fetchingBranding ? 'Fetching...' : 'Fetch'}
                </FetchButton>
              </BrandingRow>
              {!editingFacility?.name && (
                <HintText>Enter a name above first (used to store the logo).</HintText>
              )}
            </FormField>

            <FormField>
              <label>Logo</label>
              <LogoUploadRow>
                <LogoPreview>
                  {editingFacility?.logo
                    ? <img src={editingFacility.logo} alt="" />
                    : <span>{editingFacility?.name?.[0] || '?'}</span>}
                </LogoPreview>
                <UploadButtonLabel>
                  {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    disabled={uploadingLogo || !editingFacility?.name}
                    onChange={handleLogoFileChange}
                  />
                </UploadButtonLabel>
              </LogoUploadRow>
            </FormField>

            <FormField>
              <label>Language</label>
              <Select
                value={editingFacility?.language || 'en'}
                onChange={e => setEditingFacility({ ...editingFacility, language: e.target.value })}
              >
                <option value="en">English</option>
                <option value="he">Hebrew</option>
              </Select>
            </FormField>

            <FormField>
              <label>Clinic Locations</label>
              <HintText>These locations will appear in the Gister location dropdown and on doctor defaults.</HintText>
              <LocationList>
                {(editingFacility?.locations || []).map((location, index) => (
                  <LocationRow key={index}>
                    <LocationInput
                      value={location}
                      onChange={e => handleUpdateLocation(index, e.target.value)}
                      placeholder="e.g. Bayside Endoscopy Center"
                    />
                    <RemoveLocationButton type="button" onClick={() => handleRemoveLocation(index)}>
                      Remove
                    </RemoveLocationButton>
                  </LocationRow>
                ))}
                {(editingFacility?.locations || []).length === 0 && (
                  <HintText>No locations yet. Add at least one clinic location.</HintText>
                )}
              </LocationList>
              <AddLocationButton type="button" onClick={handleAddLocation}>+ Add Location</AddLocationButton>
            </FormField>
          </SettingsColumn>

          <SettingsColumn>
            <ColorPicker>
              <ColorField>
                <label>Primary Color</label>
                <ColorInput
                  type="color"
                  value={editingFacility?.color1 || '#84a4fc'}
                  onChange={e => setEditingFacility({ ...editingFacility, color1: e.target.value })}
                />
              </ColorField>
              <ColorField>
                <label>Secondary Color</label>
                <ColorInput
                  type="color"
                  value={editingFacility?.color2 || '#4A90E2'}
                  onChange={e => setEditingFacility({ ...editingFacility, color2: e.target.value })}
                />
              </ColorField>
              <ColorField>
                <label>Accent Color</label>
                <ColorInput
                  type="color"
                  value={editingFacility?.color3 || '#f02a4c'}
                  onChange={e => setEditingFacility({ ...editingFacility, color3: e.target.value })}
                />
              </ColorField>
            </ColorPicker>

            <FormField>
              <label>Gister Components (staff intake form)</label>
              <GisterComponentList>
                {GISTER_COMPONENT_OPTIONS.map(option => {
                  const selected = editingFacility?.gisterConfig || [];
                  const isChecked = selected.includes(option.key);
                  const order = selected.indexOf(option.key);
                  return (
                    <GisterComponentRow key={option.key}>
                      <label>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleGisterComponent(option.key)}
                        />
                        {option.label}
                      </label>
                      {isChecked && (
                        <ReorderButtons>
                          <button type="button" disabled={order === 0} onClick={() => moveGisterComponent(option.key, -1)}>↑</button>
                          <button type="button" disabled={order === selected.length - 1} onClick={() => moveGisterComponent(option.key, 1)}>↓</button>
                        </ReorderButtons>
                      )}
                    </GisterComponentRow>
                  );
                })}
              </GisterComponentList>
            </FormField>
          </SettingsColumn>
        </SettingsGrid>
      </SettingsCard>

      <TwoColumnLayout>
        {/* Departments Column */}
        <Column>
          <ColumnHeader>
            <h3>Departments ({facility.Departments?.length || 0})</h3>
            <AddButton small onClick={handleCreateDepartment}>
              <img src={PlusIcon} alt="" />
              Add
            </AddButton>
          </ColumnHeader>
          <SimpleTable>
            <tbody>
              {facility.Departments?.map(department => (
                <SimpleTr key={department.id}>
                  <SimpleTd>
                    <strong>{department.name}</strong>
                    <StaffCount>{department.StaffMembers?.length || 0} staff</StaffCount>
                  </SimpleTd>
                  <SimpleTd align="right">
                    <RowActions>
                      <IconBtn onClick={() => handleEditDepartment(department)}>
                        <img src={EditIcon} alt="" />
                      </IconBtn>
                      <IconBtn danger onClick={() => handleDeleteDepartment(department.id, department.name, department.StaffMembers?.length || 0)}>
                        <img src={TrashIcon} alt="" />
                      </IconBtn>
                    </RowActions>
                  </SimpleTd>
                </SimpleTr>
              ))}
              {(!facility.Departments || facility.Departments.length === 0) && (
                <SimpleTr>
                  <SimpleTd colSpan="2">
                    <EmptyState>No departments yet</EmptyState>
                  </SimpleTd>
                </SimpleTr>
              )}
            </tbody>
          </SimpleTable>
        </Column>

        {/* Staff Column */}
        <Column>
          <ColumnHeader>
            <h3>Staff Members ({totalStaff})</h3>
            <AddButton small onClick={() => facility.Departments?.[0] && handleCreateStaff(facility.Departments[0].id)} disabled={!facility.Departments?.length}>
              <img src={PlusIcon} alt="" />
              Add
            </AddButton>
          </ColumnHeader>
          <SimpleTable>
            <thead>
              <SimpleTh>Staff Member</SimpleTh>
              <SimpleTh>Department</SimpleTh>
              <SimpleTh align="right">Actions</SimpleTh>
            </thead>
            <tbody>
              {facility.Departments?.flatMap(dept => 
                dept.StaffMembers?.map(staff => (
                  <SimpleTr key={staff.id}>
                    <SimpleTd>
                      <StaffCell>
                        <div>
                          <strong>{staff.name}</strong>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>{staff.email}</div>
                        </div>
                        <RoleBadge>{staff.role}</RoleBadge>
                      </StaffCell>
                    </SimpleTd>
                    <SimpleTd>{dept.name}</SimpleTd>
                    <SimpleTd align="right">
                      <RowActions>
                        <IconBtn onClick={() => handleEditStaff(staff)}>
                          <img src={EditIcon} alt="" />
                        </IconBtn>
                        <IconBtn danger onClick={() => handleDeleteStaff(staff.id)}>
                          <img src={TrashIcon} alt="" />
                        </IconBtn>
                      </RowActions>
                    </SimpleTd>
                  </SimpleTr>
                ))
              )}
              {totalStaff === 0 && (
                <SimpleTr>
                  <SimpleTd colSpan="3">
                    <EmptyState>No staff members yet</EmptyState>
                  </SimpleTd>
                </SimpleTr>
              )}
            </tbody>
          </SimpleTable>
        </Column>
      </TwoColumnLayout>

      {/* Department Modal */}
      {modalOpen === 'department' && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>{editingDepartment?.id ? 'Edit Department' : 'Add Department'}</h2>
              <CloseButton onClick={() => setModalOpen(null)}>
                <img src={CloseIcon} alt="" />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormField>
                <label>Name</label>
                <Input
                  value={editingDepartment?.name || ''}
                  onChange={e => setEditingDepartment({ ...editingDepartment, name: e.target.value })}
                  placeholder="Department name"
                />
              </FormField>
              <FormField>
                <label>Patient Identifier</label>
                <Select
                  value={editingDepartment?.config?.patientIdentifierType || 'ssn'}
                  onChange={e => setEditingDepartment({
                    ...editingDepartment,
                    config: { ...(editingDepartment?.config || {}), patientIdentifierType: e.target.value },
                  })}
                >
                  <option value="ssn">Last 4 of SSN</option>
                  <option value="medicalRecordNumber">Medical Record Number</option>
                </Select>
              </FormField>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setModalOpen(null)}>Cancel</CancelButton>
              <SaveButton onClick={handleSaveDepartment}>Save</SaveButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Staff Modal */}
      {modalOpen === 'staff' && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>{editingStaff?.id ? 'Edit Staff Member' : 'Add Staff Member'}</h2>
              <CloseButton onClick={() => setModalOpen(null)}>
                <img src={CloseIcon} alt="" />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormField>
                <label>Name</label>
                <Input
                  value={editingStaff?.name || ''}
                  onChange={e => setEditingStaff({ ...editingStaff, name: e.target.value })}
                  placeholder="Staff name"
                />
              </FormField>
              <FormField>
                <label>Email</label>
                <Input
                  value={editingStaff?.email || ''}
                  onChange={e => setEditingStaff({ ...editingStaff, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </FormField>
              <FormField>
                <label>Phone Number</label>
                <Input
                  type="tel"
                  value={editingStaff?.phoneNumber || ''}
                  onChange={e => setEditingStaff({ ...editingStaff, phoneNumber: e.target.value })}
                  placeholder="+1 555 123 4567"
                />
              </FormField>
              <FormField>
                <label>Role</label>
                <Input
                  value={editingStaff?.role || ''}
                  onChange={e => setEditingStaff({ ...editingStaff, role: e.target.value })}
                  placeholder="coordinator"
                />
              </FormField>
              <FormField>
                <label>Password {editingStaff?.id ? '(leave blank to keep current)' : '*'}</label>
                <Input
                  type="password"
                  value={editingStaff?.password || ''}
                  onChange={e => setEditingStaff({ ...editingStaff, password: e.target.value })}
                  placeholder="Enter password"
                />
              </FormField>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setModalOpen(null)}>Cancel</CancelButton>
              <SaveButton onClick={handleSaveStaff}>Save</SaveButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

    </Container>
  );
}
